// =============================================================================
// app/src/lib/navegacaoDoCatalogo.ts — os passos da navegação por TIPO e por
// CARRO (092, ADR-009). Puro: a tela pergunta "onde estou e o que vem agora?",
// e a resposta é testada sem banco.
//
//   por tipo:  Tipo → Montadora → Modelo → peças      ("Calotas → Fiat → Palio")
//   por carro: Montadora → Modelo → Tipo → peças      ("Fiat → Palio → Óleo")
//
// Tipo universal (tapete, produto de limpeza) não depende do carro: escolher o
// tipo já mostra as peças. "Sem carro" é o atalho para as peças do tipo que
// não têm aplicação (a calota universal), para elas não sumirem do caminho.
// =============================================================================
import type { ItemDaNavegacao, ModoDoTipo } from '@core/types';

export type Comeco = 'tipo' | 'carro';

export interface Escolhido {
  chave: string;
  nome: string;
}

export interface EstadoDaNavegacao {
  comeco: Comeco;
  tipo: (Escolhido & { modo?: ModoDoTipo }) | null;
  montadora: Escolhido | null;
  modelo: Escolhido | null;
  /** Atalho "sem carro": as peças do tipo sem aplicação, ou todas do tipo. */
  semCarro: boolean;
}

export const INICIO = (comeco: Comeco): EstadoDaNavegacao =>
  ({ comeco, tipo: null, montadora: null, modelo: null, semCarro: false });

/** O que a tela mostra agora: uma lista para escolher, ou as peças. */
export type Passo =
  | { mostra: 'tipos'; consulta: { tipo: null; montadora: null; modelo: string | null } }
  | { mostra: 'montadoras'; consulta: { tipo: string | null; montadora: null; modelo: null } }
  | { mostra: 'modelos'; consulta: { tipo: string | null; montadora: string; modelo: null } }
  | { mostra: 'pecas'; tipo: string; modelo: string | null };

export function passo(e: EstadoDaNavegacao): Passo {
  if (e.comeco === 'tipo') {
    if (!e.tipo) return { mostra: 'tipos', consulta: { tipo: null, montadora: null, modelo: null } };
    if (e.tipo.modo === 'universal' || e.semCarro) return { mostra: 'pecas', tipo: e.tipo.chave, modelo: null };
    if (!e.montadora) return { mostra: 'montadoras', consulta: { tipo: e.tipo.chave, montadora: null, modelo: null } };
    if (!e.modelo) return { mostra: 'modelos', consulta: { tipo: e.tipo.chave, montadora: e.montadora.chave, modelo: null } };
    return { mostra: 'pecas', tipo: e.tipo.chave, modelo: e.modelo.chave };
  }
  if (!e.montadora) return { mostra: 'montadoras', consulta: { tipo: null, montadora: null, modelo: null } };
  if (!e.modelo) return { mostra: 'modelos', consulta: { tipo: null, montadora: e.montadora.chave, modelo: null } };
  if (!e.tipo) return { mostra: 'tipos', consulta: { tipo: null, montadora: null, modelo: e.modelo.chave } };
  return { mostra: 'pecas', tipo: e.tipo.chave, modelo: e.modelo.chave };
}

/** As migalhas, na ordem do caminho, para voltar a qualquer passo. */
export function trilha(e: EstadoDaNavegacao): Array<{ rotulo: string; volta: EstadoDaNavegacao }> {
  const ordem: Array<'tipo' | 'montadora' | 'modelo'> =
    e.comeco === 'tipo' ? ['tipo', 'montadora', 'modelo'] : ['montadora', 'modelo', 'tipo'];
  const saida: Array<{ rotulo: string; volta: EstadoDaNavegacao }> = [];
  let acumulado = INICIO(e.comeco);
  for (const nivel of ordem) {
    const v = e[nivel];
    if (!v) break;
    acumulado = { ...acumulado, [nivel]: v };
    saida.push({ rotulo: v.nome, volta: acumulado });
  }
  if (e.semCarro && e.tipo) saida.push({ rotulo: 'Sem carro', volta: e });
  return saida;
}

/** Os tipos agrupados pelo grupo (categoria), na ordem que o banco mandou. */
export function agruparTipos(tipos: ItemDaNavegacao[]): Array<{ grupo: string; tipos: ItemDaNavegacao[] }> {
  const grupos: Array<{ grupo: string; tipos: ItemDaNavegacao[] }> = [];
  for (const t of tipos) {
    const nome = t.grupo ?? 'Outros';
    const g = grupos.find((x) => x.grupo === nome);
    if (g) g.tipos.push(t); else grupos.push({ grupo: nome, tipos: [t] });
  }
  return grupos;
}

/**
 * As três listas da tela de peças. Com o atalho "sem carro", só as peças SEM
 * aplicação (a calota universal) — antes ele listava todas do tipo como se
 * servissem em qualquer carro (revisão do #254).
 */
export function separarPecas<P extends { casa: boolean | null; tem_carro: boolean }>(
  pecas: P[],
  semCarro: boolean,
): { servem: P[]; emParte: P[]; outras: P[] } {
  const base = semCarro ? pecas.filter((p) => !p.tem_carro) : pecas;
  return {
    servem: base.filter((p) => p.casa === true),
    emParte: base.filter((p) => p.casa === null),
    outras: base.filter((p) => p.casa === false),
  };
}

/** "G4 · 1.0 8v EA111 flex · 2006-2014", para dizer onde a peça serve. */
export function ondeServe(o: { geracao: string | null; anos: string | null; motor: string | null }): string {
  return [o.geracao, o.motor, o.anos].filter(Boolean).join(' · ');
}

/** "3 peças", "1 peça", "sem peça · 2 especificações". */
export function contagem(i: Pick<ItemDaNavegacao, 'pecas' | 'especificacoes'>): string {
  const pecas = i.pecas === 0 ? 'sem peça' : i.pecas === 1 ? '1 peça' : `${i.pecas} peças`;
  if (!i.especificacoes) return pecas;
  return `${pecas} · ${i.especificacoes === 1 ? '1 especificação' : `${i.especificacoes} especificações`}`;
}
